import { useEffect } from 'react';
import { message } from 'antd';

const NetworkErrorSuppressor = ({ children }) => {
  useEffect(() => {
    // Track shown messages to avoid spam
    const shownMessages = new Set();
    
    // Override console.error to show network error popups
    const originalConsoleError = console.error;
    
    console.error = (...args) => {
      // Check if any argument contains network error indicators
      const isNetworkError = args.some(arg => 
        typeof arg === 'string' && (
          arg.includes('NetworkError') ||
          arg.includes('Failed to fetch') ||
          arg.includes('ERR_NETWORK') ||
          arg.includes('fetch') ||
          arg.includes('Network Error') ||
          arg.includes('ERR_INTERNET_DISCONNECTED')
        )
      );
      
      if (isNetworkError) {
        // Create a unique key for this error to avoid spam
        const errorKey = args.join(' ').substring(0, 100);
        
        if (!shownMessages.has(errorKey)) {
          shownMessages.add(errorKey);
          
          // Show user-friendly network error message
          message.error({
            content: 'Network error - check internet connection',
            duration: 4,
            key: 'network-error'
          });
          
          // Clear the message from tracking after 10 seconds to allow showing again
          setTimeout(() => {
            shownMessages.delete(errorKey);
          }, 10000);
        }
        
        // Still log in development mode
        if (process.env.NODE_ENV === 'development') {
          originalConsoleError.apply(console, ['[NETWORK ERROR]', ...args]);
        }
      } else {
        // Show non-network errors normally
        originalConsoleError.apply(console, args);
      }
    };
    
    // Handle unhandled promise rejections (like fetch errors and axios errors)
    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      
      // Check for axios errors from WebOrkDashboard that are marked as suppressible
      const isAxiosNetworkError = error && (
        error._suppressUserError === true ||
        error._isHandled === true ||
        (error.code && ['ECONNABORTED', 'ERR_NETWORK', 'ENOTFOUND', 'ECONNREFUSED'].includes(error.code)) ||
        (error.message && error.message === 'Network Error') ||
        !error.response // No response means network issue
      );
      
      // Check for general fetch/network errors
      const isFetchError = error && (
        error.message?.includes('fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Failed to fetch') ||
        error.name === 'TypeError' && error.message?.includes('fetch')
      );
      
      if (isAxiosNetworkError || isFetchError) {
        // Create different error keys for different types
        let errorKey;
        let messageContent;
        
        if (isAxiosNetworkError) {
          errorKey = 'axios-network-error';
          messageContent = 'Could not fetch data from server';
        } else {
          errorKey = error.message?.substring(0, 100) || 'fetch-network-error';
          messageContent = 'Network error - could not fetch data';
        }
        
        if (!shownMessages.has(errorKey)) {
          shownMessages.add(errorKey);
          
          message.error({
            content: messageContent,
            duration: 4,
            key: errorKey
          });
          
          // Clear after 15 seconds to allow showing again if needed
          setTimeout(() => {
            shownMessages.delete(errorKey);
          }, 15000);
        }
        
        // Prevent the default unhandled rejection behavior
        event.preventDefault();
      }
    };
    
    // Add listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    // Listen for custom axios error events that might be dispatched
    const handleAxiosError = (event) => {
      if (event.detail && event.detail._suppressUserError) {
        const errorKey = 'axios-api-error';
        
        if (!shownMessages.has(errorKey)) {
          shownMessages.add(errorKey);
          
          message.error({
            content: 'API error - could not connect to WebOrk server',
            duration: 4,
            key: errorKey
          });
          
          setTimeout(() => {
            shownMessages.delete(errorKey);
          }, 15000);
        }
      }
    };
    
    window.addEventListener('axios-error', handleAxiosError);
    
    return () => {
      console.error = originalConsoleError;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('axios-error', handleAxiosError);
    };
  }, []);
  
  return children;
};

export default NetworkErrorSuppressor;
